import { useLayoutEffect, useRef } from 'react';
import { gsap } from '../gsapSetup';

const MAIN =
  'M 72 560 L 72 412 L 188 412 L 188 268 L 332 268 L 332 168 L 488 168 L 488 84 L 568 84';

export default function HeroPath() {
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const path = root?.querySelector('.hero-path-main');
    const traveler = root?.querySelector('.hero-path-dot');
    const nodes = root?.querySelectorAll('.hero-node');
    const labels = root?.querySelectorAll('.hero-label');
    const branches = root?.querySelectorAll('.hero-branch');
    if (!path || typeof path.getTotalLength !== 'function') return undefined;

    let length = 0;
    try {
      length = path.getTotalLength();
    } catch {
      return undefined;
    }
    if (!length) return undefined;

    const ctx = gsap.context(() => {
      gsap.set(path, { strokeDasharray: length, strokeDashoffset: length });
      gsap.set([traveler, nodes, labels], { opacity: 0 });
      gsap.set(branches, { opacity: 0 });

      gsap
        .timeline({ defaults: { ease: 'power2.out' } })
        .to(path, { strokeDashoffset: 0, duration: 2.1, ease: 'power2.inOut' })
        .to(branches, { opacity: 1, duration: 0.5 }, '-=1.1')
        .to(nodes, { opacity: 1, stagger: 0.08, duration: 0.28 }, '-=1.4')
        .to(labels, { opacity: 1, stagger: 0.08, duration: 0.28 }, '-=1.1')
        .to(traveler, { opacity: 1, duration: 0.2 }, '-=0.4');

      gsap.to(traveler, {
        motionPath: { path, align: path, alignOrigin: [0.5, 0.5] },
        duration: 9,
        ease: 'none',
        repeat: -1,
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <svg
      className="hero-diagram"
      viewBox="0 0 640 640"
      fill="none"
      aria-hidden="true"
      ref={rootRef}
    >
      <defs>
        <linearGradient id="heroPathGlow" x1="72" y1="560" x2="568" y2="84">
          <stop offset="0%" stopColor="#6ee7ff" />
          <stop offset="55%" stopColor="#c8f542" />
          <stop offset="100%" stopColor="#f4f7ef" />
        </linearGradient>
      </defs>

      {[80, 160, 240, 320, 400, 480, 560].map((n) => (
        <g key={n}>
          <line className="hero-grid" x1="40" y1={n} x2="600" y2={n} />
          <line className="hero-grid" x1={n} y1="40" x2={n} y2="600" />
        </g>
      ))}

      <path className="hero-branch" d="M 188 412 L 188 500 L 268 500" />
      <path className="hero-branch" d="M 332 268 L 420 268 L 420 200" />
      <path className="hero-branch" d="M 488 168 L 560 168 L 560 240" />

      <path className="hero-path-main" d={MAIN} />

      <g className="hero-node">
        <rect x="64" y="552" width="16" height="16" />
        <text className="hero-label" x="88" y="564">
          Discover
        </text>
      </g>
      <g className="hero-node">
        <rect x="180" y="404" width="16" height="16" />
        <text className="hero-label" x="204" y="392">
          Design
        </text>
      </g>
      <g className="hero-node">
        <rect x="324" y="260" width="16" height="16" />
        <text className="hero-label" x="348" y="248">
          Build
        </text>
      </g>
      <g className="hero-node">
        <rect x="480" y="160" width="16" height="16" />
        <text className="hero-label" x="504" y="148">
          Launch
        </text>
      </g>
      <g className="hero-node">
        <rect x="560" y="76" width="16" height="16" />
        <text className="hero-label" x="504" y="68">
          Scale
        </text>
      </g>

      <rect className="hero-path-dot" x="-5" y="-5" width="10" height="10" rx="1" />
    </svg>
  );
}
